import { isPreview } from "./preview";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@codelittinc/carbon-design-system";
import {
  MAX_RANGES_PER_REPORT,
  VIDEO_VIEWER_HEADER,
  videoPath,
  type TrainingItem,
  type VideoPlayback,
  type VideoProgress,
  type VideoProgressRequest,
  type WatchedRange,
} from "@carbon-os/server/contract";
import { trackTraining } from "./analytics";
import { mergeRanges, readOutbox, writeOutbox, outboxFor, type Outbox, type OutboxEntry, onOutboxesCleared, clearOutboxes } from "./outbox";
import { browserDeps } from "./api";
import { beginSignIn, TRAINING_SIGNIN_ATTEMPT_KEY } from "./signin";
import { failedOf, fetchNow, TRACK_EXPIRED, type Failed, type TrainingControls } from "./training";

/**
 * Uploaded training videos (carbon-gatekeeper#171). The browser asks carbon-os
 * for a presigned URL and plays it straight from the bucket; carbon-os never
 * carries the bytes. What was watched goes back as ranges, batched, and
 * Gatekeeper marks the item done at 90% unless it has a quiz.
 */

/** At most one progress report this often while playing; pause, end, close and pagehide send at once. */
export const REPORT_EVERY_MS = 15_000;
/** Media errors that may each fetch a fresh URL within a minute, before the player gives up. */
export const MAX_REFRESHES_PER_MINUTE = 3;
/** A fresh URL is fetched this long before the current one expires, while the player is open. */
export const REFRESH_BEFORE_EXPIRY_MS = 5 * 60_000;
/** Never sooner than this, so a URL that is already close to expiring cannot set off a loop of requests. */
const MIN_REFRESH_WAIT_MS = 60_000;
/** A background refresh that failed is tried again after this, doubling, up to REFRESH_RETRY_MAX_MS. */
export const REFRESH_RETRY_MS = 30_000;
const REFRESH_RETRY_MAX_MS = 5 * 60_000;
/** A failed progress report is tried again after this, doubling, up to REPORT_RETRY_MAX_MS. */
const REPORT_RETRY_MAX_MS = 120_000;
/** Consecutive failed reports before the dialog says progress isn't being saved. */
export const FAILURES_BEFORE_NOTICE = 2;

/**
 * Gatekeeper's plausibility rule (carbon-gatekeeper#171): the new coverage one
 * report adds may not exceed the wall seconds since the previous accepted
 * report × 2 (the fastest playback rate) + 30 s, and only the 30 s with no
 * previous report. Ranges can only overstate new coverage, so a batch whose
 * total length stays within this, with a few seconds' margin, is never refused.
 */
export const PLAUSIBLE_RATE = 2;
export const PLAUSIBLE_GRACE_SECONDS = 25;

export const VIDEO_FAILED = "This video couldn't be played right now.";
export const VIDEO_GONE = "This video is no longer available";
export const NOT_SAVED = "We couldn't save your progress — it will be retried";
export const SIGN_IN_TO_SAVE = "Sign in again to keep your progress";

type Call<T> = { ok: true; body: T } | Failed;

/** GET …/playback: a fresh presigned URL. `status` is 0 when the request never arrived. */
export async function loadPlayback(fetchImpl: typeof fetch, materialId: string): Promise<Call<VideoPlayback>> {
  let res: Response;
  try {
    res = await fetchImpl(videoPath(materialId, "playback"), {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
  } catch {
    return { ok: false, status: 0 };
  }
  if (res.status !== 200) return failedOf(res);
  const body = (await res.json().catch(() => null)) as VideoPlayback | null;
  if (!body || typeof body.url !== "string") return { ok: false, status: res.status };
  return { ok: true, body };
}

/** PUT …/progress. `keepalive` lets it outlive the page (pagehide, a closing dialog); `sendBeacon` cannot PUT. */
export async function sendProgress(
  fetchImpl: typeof fetch,
  materialId: string,
  progress: VideoProgressRequest,
  { keepalive = false, viewerKey = null }: { keepalive?: boolean; viewerKey?: string | null } = {},
): Promise<Call<VideoProgress>> {
  let res: Response;
  try {
    res = await fetchImpl(videoPath(materialId, "progress"), {
      method: "PUT",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json", [VIDEO_VIEWER_HEADER]: viewerKey ?? "" },
      body: JSON.stringify(progress),
      ...(keepalive ? { keepalive: true } : {}),
    });
  } catch {
    return { ok: false, status: 0 };
  }
  if (res.status !== 200) return failedOf(res);
  const body = (await res.json().catch(() => null)) as VideoProgress | null;
  if (!body) return { ok: false, status: res.status };
  return { ok: true, body };
}


// ---- The reporter ----

/** Refusals no retry can fix: the batch is dropped instead of being sent again forever. */
const DROPPED = new Set(["implausible_progress", "invalid_progress", "unknown_material", "viewer_changed", "track_expired"]);

/** Whether what was watched is reaching Gatekeeper. */
export type SaveStatus = "ok" | "failing" | "signin";

export interface ReporterDeps {
  /** Bound to every report even when localStorage is unavailable. */
  viewerKey?: string | null;
  fetch: typeof fetch;
  /** Each answer Gatekeeper gives, in the order they arrive. */
  onProgress: (progress: VideoProgress) => void;
  /** A batch thrown away because Gatekeeper refused it for good. */
  onDropped: (code: string, status: number) => void;
  onStatus?: (status: SaveStatus) => void;
  /** The signed-in person's outbox, where unconfirmed ranges are kept once the page may go away; null keeps nothing. */
  outbox?: Outbox | null;
  /** Stored with the outbox entry, for the analytics of whatever sends it later. */
  trackId?: string;
  /** Ranges from the outbox, sent before anything new. */
  seed?: OutboxEntry;
  now?: () => number;
}

export interface Reporter {
  /** A watched stretch from the player, and where the playhead is. */
  add: (range: WatchedRange, positionSeconds: number) => void;
  /** Sends what is queued now, past the 15 s pace but not a failure's backoff (pause, end). */
  flush: () => void;
  /** The page is going away or out of sight: one send now, with keepalive; then the usual pace, still with keepalive. */
  hide: () => void;
  /** Back in sight. */
  show: () => void;
  /** The player closed: everything left goes, chunked, with keepalive; after a failure the outbox keeps the rest. */
  close: () => void;
}

/** The one reporter per material in this page, a closed one still sending its last chunks included. */
const live = new Map<string, { reporter: Reporter; adopt: (deps: ReporterDeps) => void; forget: () => void; viewer: () => string | null }>();

export function isReporting(materialId: string): boolean {
  return live.has(materialId);
}

/** Cancels reporters and callbacks while preserving persisted progress for the real user. */
export function forgetReporters(): void {
  for (const { forget } of live.values()) forget();
  live.clear();
}

// Sign-out cancels callbacks too, so a delayed answer cannot restore cleared progress.
onOutboxesCleared(forgetReporters);

/**
 * The reporter for a material: the live one, adopted (reopened, with these
 * callbacks) when there is one, so there is never a second sender for the
 * same material; a new one otherwise, seeded from the outbox.
 */
export function reporterFor(materialId: string, deps: ReporterDeps): Reporter {
  const current = live.get(materialId);
  if (current && current.viewer() !== (deps.outbox?.viewerKey ?? null)) {
    current.forget();
    live.delete(materialId);
  } else if (current) {
    current.adopt(deps);
    return current.reporter;
  }
  return createReporter(materialId, deps);
}

/**
 * Batches one video's watched ranges into PUT …/progress.
 *
 * - One report at a time, including hide and close. Unconfirmed ranges are
 *   persisted before the page can go away and the next report waits.
 * - Each batch stays within Gatekeeper's plausibility budget, so the reporter
 *   never provokes `implausible_progress` itself; a longer backlog goes in
 *   several reports.
 * - A failed batch is retried as it was, first, after a backoff that always
 *   applies. A closed reporter stops at its first failure (the outbox keeps
 *   the rest), and leaves `live` once nothing is out, whatever order its
 *   answers come back.
 * - Hidden, it keeps the 15 s pace, and writes the outbox at most once per
 *   pace; a hide or close always writes it at once.
 */
export function createReporter(materialId: string, initial: ReporterDeps): Reporter {
  let deps = initial;
  const now = () => (deps.now ?? Date.now)();
  let pending: WatchedRange[] = deps.seed ? mergeRanges(deps.seed.ranges) : [];
  let position = deps.seed?.positionSeconds ?? 0;
  /** A batch that failed, sent again as it was before anything else. */
  let retry: WatchedRange[] | null = null;
  let inflight: WatchedRange[] | null = null;
  let urgentPending = false;
  let cancelled = false;
  /** When this reporter observed an acceptance. An outbox never proves Gatekeeper saw an earlier report. */
  let lastAccepted: number | null = null;
  let lastSent = Number.NEGATIVE_INFINITY;
  let failures = 0;
  let retryAt = 0;
  let status: SaveStatus = "ok";
  let timer: ReturnType<typeof setTimeout> | null = null;
  let hidden = false;
  let closed = false;
  /** Closed and failed: nothing more is sent; the outbox has it. */
  let stopped = false;
  /** Mirroring into the outbox, from the first hide or close (or an outbox seed) on. */
  let persisting = Boolean(deps.seed);
  let lastPersist = Number.NEGATIVE_INFINITY;

  const unconfirmed = () => [...(retry ?? []), ...(inflight ?? []), ...pending];
  const persist = (force: boolean) => {
    if (!persisting) return;
    const ranges = unconfirmed();
    // Hidden and playing, a write per segment would be one every few seconds:
    // once per pace is enough. Emptying it is always written, so nothing
    // confirmed is ever sent again.
    if (!force && !closed && ranges.length > 0 && now() - lastPersist < REPORT_EVERY_MS) return;
    lastPersist = now();
    writeOutbox(
      deps.outbox ?? null,
      materialId,
      ranges.length > 0 ? { ranges, positionSeconds: position, trackId: deps.trackId ?? "", savedAt: now() } : null,
    );
  };
  const setStatus = (next: SaveStatus) => {
    if (next === status) return;
    status = next;
    deps.onStatus?.(next);
  };
  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  /** A closed reporter leaves `live` once nothing is out and nothing more will be sent. */
  const settle = () => {
    if (closed && !inflight && (stopped || unconfirmed().length === 0) && live.get(materialId)?.reporter === self) {
      clearTimer();
      live.delete(materialId);
    }
  };

  /** Seconds of coverage the next report may claim. */
  const budget = () =>
    lastAccepted === null
      ? PLAUSIBLE_GRACE_SECONDS
      : (Math.max(0, now() - lastAccepted) / 1000) * PLAUSIBLE_RATE + PLAUSIBLE_GRACE_SECONDS;

  /** The next batch from `pending`: at most MAX_RANGES_PER_REPORT ranges and `seconds` of coverage, splitting a range at the limit. */
  const take = (seconds: number): WatchedRange[] => {
    pending = mergeRanges(pending);
    const batch: WatchedRange[] = [];
    let left = seconds;
    while (pending.length > 0 && batch.length < MAX_RANGES_PER_REPORT && left >= 0.5) {
      const [start, end] = pending[0] as WatchedRange;
      if (end - start <= left) {
        batch.push([start, end]);
        pending.shift();
        left -= end - start;
      } else {
        const cut = Math.floor(Math.min(start + left, end - 0.5) * 10) / 10;
        if (cut - start < 0.5) break;
        batch.push([start, cut]);
        pending[0] = [cut, end];
        left = 0;
      }
    }
    return batch;
  };

  const send = async (batch: WatchedRange[], { keepalive }: { keepalive: boolean }) => {
    inflight = batch;
    lastSent = now();
    const sent = await sendProgress(deps.fetch, materialId, { ranges: batch, positionSeconds: position }, { keepalive, viewerKey: deps.viewerKey ?? deps.outbox?.viewerKey ?? null });
    inflight = null;
    if (cancelled) return;

    if (sent.ok) {
      lastAccepted = now();
      failures = 0;
      setStatus("ok");
      deps.onProgress(sent.body);
    } else if (sent.code && DROPPED.has(sent.code)) {
      deps.onDropped(sent.code, sent.status);
    } else {
      failures += 1;
      retryAt = now() + Math.min(REPORT_EVERY_MS * 2 ** (failures - 1), REPORT_RETRY_MAX_MS);
      if (retry === null) retry = batch;
      else pending = [...batch, ...pending];
      if (closed) stopped = true;
      if (sent.status === 401) setStatus("signin");
      else if (failures >= FAILURES_BEFORE_NOTICE) setStatus("failing");
    }
    if (cancelled) return;
    persist(false);
    const urgent = urgentPending;
    urgentPending = false;
    pump(urgent ? "urgent" : "paced");
    settle();
  };

  function pump(mode: "paced" | "flush" | "urgent") {
    clearTimer();
    if (stopped || cancelled) return;
    const urgent = mode === "urgent" || closed;
    if (inflight) {
      if (mode === "urgent") urgentPending = true;
      return;
    }
    if (retry === null && pending.length === 0) return;
    // A failure's backoff always applies; the 15 s pace only while open, and not for a pause, a hide or a close.
    const until = failures > 0 ? retryAt : urgent || mode === "flush" ? 0 : lastSent + REPORT_EVERY_MS;
    const wait = until - now();
    if (wait > 0) {
      timer = setTimeout(() => {
        timer = null;
        pump(closed ? "urgent" : "paced");
      }, wait);
      return;
    }
    let batch: WatchedRange[];
    if (retry) {
      batch = retry;
      retry = null;
    } else batch = take(budget());
    if (batch.length > 0) void send(batch, { keepalive: hidden || closed });
  }

  const self: Reporter = {
    add(range, positionSeconds) {
      if (cancelled) return;
      pending.push(range);
      position = positionSeconds;
      if (closed && !live.has(materialId)) live.set(materialId, entry);
      persist(false);
      pump(closed ? "urgent" : "paced");
      settle();
    },
    flush() {
      pump("flush");
    },
    hide() {
      if (cancelled) return;
      hidden = true;
      persisting = true;
      persist(true);
      pump("urgent");
    },
    show() {
      hidden = false;
      if (!closed) pump("paced");
    },
    close() {
      if (cancelled) return;
      closed = true;
      persisting = true;
      persist(true);
      pump("urgent");
      settle();
    },
  };

  const entry = {
    reporter: self,
    viewer: () => deps.outbox?.viewerKey ?? null,
    forget() {
      clearTimer();
      stopped = true;
      cancelled = true;
    },
    /** Reopened: the same queue and state, the new callbacks, open again, and sending again if it had stopped. */
    adopt(next: ReporterDeps) {
      deps = { ...next, ...(next.outbox === undefined ? { outbox: deps.outbox ?? null } : {}) };
      closed = false;
      stopped = false;
      if (status !== "ok") deps.onStatus?.(status);
      pump("paced");
    },
  };
  live.set(materialId, entry);
  return self;
}

/**
 * Sends what this person's outbox holds for every material but `skip` (whose
 * own reporter takes its entry) and those with a reporter at work: each through
 * a closed reporter, so it goes in plausible chunks, with keepalive, and
 * whatever fails stays for next time.
 */
export function drainOutbox(
  outbox: Outbox | null,
  skip: string,
  deps: Pick<ReporterDeps, "fetch" | "now"> & {
    onProgress: (materialId: string, trackId: string, progress: VideoProgress) => void;
    onDropped: (materialId: string, code: string, status: number) => void;
  },
): void {
  for (const [materialId, entry] of Object.entries(readOutbox(outbox))) {
    if (materialId === skip || live.has(materialId)) continue;
    createReporter(materialId, {
      fetch: deps.fetch,
      ...(deps.now ? { now: deps.now } : {}),
      outbox,
      viewerKey: outbox?.viewerKey ?? null,
      trackId: entry.trackId,
      seed: entry,
      onProgress: (progress) => deps.onProgress(materialId, entry.trackId, progress),
      onDropped: (code, status) => deps.onDropped(materialId, code, status),
    }).close();
  }
}

export type VideoView =
  | { kind: "closed" }
  | { kind: "loading" }
  /** `startAt` is fixed when the player mounts; a refreshed `playback.url` keeps the position. */
  | { kind: "playing"; playback: VideoPlayback; startAt: number }
  | { kind: "failed"; retrying: boolean };

/** A line in the dialog that does not stop the video. */
export type VideoNotice = "signin" | "not_saved" | null;

/** The one video that is open. Async results for any other are dropped. */
interface Session {
  viewerKey: string | null;
  request: number;
  item: TrainingItem;
  trackId: string;
  reporter: Reporter;
  /** When each fresh URL was asked for after a media error, for the rate limit. */
  refreshes: number[];
  /** Said once that the track ended, however many batches are refused after. */
  expiredNotice?: boolean;
  /** Where the playhead last was, so Try again after a failure starts there. */
  position: number | null;
  /** The current URL's expiry on this page's monotonic clock (`performance.now()`), never the wall clock. */
  expiresAt: number;
  /** The next background refresh, or its retry. */
  refreshTimer: ReturnType<typeof setTimeout> | null;
  /** The current URL running out with no fresh one to take its place. */
  expiryTimer: ReturnType<typeof setTimeout> | null;
  /** Closed: kept as `session.current` until the next opens, only for the player's last stretch. */
  closed: boolean;
}

const active = (current: Session | null, s: Session) => current === s && !s.closed;

/** The page's monotonic clock: a wrong or changed wall clock cannot move it. */
const monotonic = () => performance.now();

export interface VideoControls {
  view: VideoView;
  /** The video that is open, or was last. */
  item: TrainingItem | null;
  notice: VideoNotice;
  open: (item: TrainingItem, trackId: string) => void;
  close: () => void;
  onSegment: (segment: { start: number; end: number }, positionSeconds: number) => void;
  /** The player paused or reached the end: report now. */
  onStop: () => void;
  onError: (positionSeconds: number) => void;
  tryAgain: () => void;
  /** Leaves for sign-in; what was watched goes with keepalive, or stays in the outbox. */
  signIn: () => void;
}

/**
 * The open video: one at a time, on a dialog, the way `useQuiz` runs the quiz.
 * Every answer from Gatekeeper goes to `watched`, which moves the row's
 * progress and, the first time it says done, the item itself.
 */
export function useVideo({
  watched,
  refresh,
  retry,
  viewerKey,
}: Pick<TrainingControls, "watched" | "refresh" | "retry"> & {
  /** The signed-in person's key from /api/training: their outbox, and nobody else's. */
  viewerKey: string | null;
}): VideoControls {
  const [view, setView] = useState<VideoView>({ kind: "closed" });
  const [item, setItem] = useState<TrainingItem | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("ok");
  // A background refresh came back 401: the video plays on, but the next URL will not come.
  const [refreshSignIn, setRefreshSignIn] = useState(false);
  const latest = useRef<VideoView>(view);
  const session = useRef<Session | null>(null);
  const currentViewer = useRef(viewerKey);
  currentViewer.current = viewerKey;
  const previousViewer = useRef(viewerKey);

  const set = useCallback((next: VideoView) => {
    latest.current = next;
    setView(next);
  }, []);

  const clearTimers = (s: Session) => {
    if (s.refreshTimer !== null) clearTimeout(s.refreshTimer);
    if (s.expiryTimer !== null) clearTimeout(s.expiryTimer);
    s.refreshTimer = null;
    s.expiryTimer = null;
  };

  const end = useCallback((s: Session) => {
    clearTimers(s);
    s.closed = true;
    s.reporter.close();
  }, []);

  const fail = useCallback(
    (s: Session, code: string | undefined, status: number) => {
      trackTraining("training_video_error", { material_id: s.item.materialId, code: code ?? null, status });
      if (!active(session.current, s)) return;
      if (status === 401) {
        // Signed out with nothing playing: the training page's own sign-in takes it from here.
        end(s);
        set({ kind: "closed" });
        retry();
      } else if (code === "unknown_material") {
        end(s);
        set({ kind: "closed" });
        toast.error(VIDEO_GONE);
        refresh();
      } else {
        clearTimers(s);
        set({ kind: "failed", retrying: false });
      }
    },
    [end, refresh, retry, set],
  );

  /** A new URL in place: the player mounts with it, or swaps it in keeping the position; and its timers. */
  const apply = useCallback(
    (s: Session, playback: VideoPlayback, refreshUrl: (s: Session, attempt: number) => void) => {
      const current = latest.current;
      const startAt = current.kind === "playing" ? current.startAt : (s.position ?? playback.resumeAtSeconds);
      // A same-second signature may repeat. A fragment changes the media src
      // without changing the signed HTTP request, so the player reloads it.
      const url = `${playback.url.split("#")[0]}#carbonos-video=${s.request}`;
      set({ kind: "playing", playback: { ...playback, url }, startAt });
      setRefreshSignIn(false);
      clearTimers(s);
      // From the time left, as the server counted it, and this page's monotonic
      // clock: the browser's wall clock never decides when the URL runs out.
      const left = Math.max(0, playback.expiresInSeconds) * 1000;
      s.expiresAt = monotonic() + left;
      const wait = left - REFRESH_BEFORE_EXPIRY_MS;
      s.refreshTimer = setTimeout(() => refreshUrl(s, 0), Math.min(Math.max(wait, MIN_REFRESH_WAIT_MS), 2_147_483_647));
      // Only the URL actually running out, with no fresh one in its place, stops the video.
      s.expiryTimer = setTimeout(
        () => {
          s.expiryTimer = null;
          if (active(session.current, s) && latest.current.kind === "playing") fail(s, "url_expired", 0);
        },
        Math.min(left, 2_147_483_647),
      );
    },
    [fail, set],
  );

  /**
   * The refresh ahead of expiry, while the video plays. A failure never touches
   * the player: it is tried again, backing off, until the URL runs out. A 401
   * says so in the dialog and the video carries on.
   */
  const refreshUrl = useCallback(
    async (s: Session, attempt: number) => {
      s.refreshTimer = null;
      if (!active(session.current, s)) return;
      const request = ++s.request;
      const loaded = await loadPlayback(fetchNow, s.item.materialId);
      if (!active(session.current, s) || s.request !== request || s.viewerKey !== currentViewer.current) return;
      if (loaded.ok) return apply(s, loaded.body, (next, n) => void refreshUrl(next, n));
      trackTraining("training_video_error", { material_id: s.item.materialId, code: loaded.code ?? null, status: loaded.status });
      if (loaded.status === 401) setRefreshSignIn(true);
      // Gone from Gatekeeper: nothing to refresh, and it plays on until the URL runs out.
      if (loaded.code === "unknown_material") return;
      const delay = Math.min(REFRESH_RETRY_MS * 2 ** attempt, REFRESH_RETRY_MAX_MS);
      if (monotonic() + delay < s.expiresAt) {
        s.refreshTimer = setTimeout(() => void refreshUrl(s, attempt + 1), delay);
      }
    },
    [apply],
  );

  /** A URL the player needs now: the first, after a media error, or after Try again. */
  const load = useCallback(
    async (s: Session) => {
      const request = ++s.request;
      const loaded = await loadPlayback(fetchNow, s.item.materialId);
      if (!active(session.current, s) || s.request !== request || s.viewerKey !== currentViewer.current) return;
      if (!loaded.ok) return fail(s, loaded.code, loaded.status);
      apply(s, loaded.body, (next, n) => void refreshUrl(next, n));
    },
    [apply, fail, refreshUrl],
  );

  const open = useCallback(
    (opened: TrainingItem, trackId: string) => {
      if (latest.current.kind !== "closed") return;
      const outbox = isPreview() ? null : outboxFor(viewerKey);
      // A reporter for this video still at work is adopted, and keeps its own entry.
      const seed = isReporting(opened.materialId) ? undefined : readOutbox(outbox)[opened.materialId];
      // Whatever an earlier page could not send, for any other video, goes first.
      drainOutbox(outbox, opened.materialId, {
        fetch: fetchNow,
        onProgress: (materialId, otherTrack, progress) => {
          if (currentViewer.current === viewerKey) watched(materialId, otherTrack, progress, viewerKey);
        },
        onDropped: (materialId, code, status) => trackTraining("training_video_error", { material_id: materialId, code, status }),
      });
      setSaveStatus("ok");
      setRefreshSignIn(false);
      const s: Session = {
        viewerKey,
        request: 0,
        item: opened,
        trackId,
        refreshes: [],
        position: null,
        expiresAt: 0,
        refreshTimer: null,
        expiryTimer: null,
        closed: false,
        reporter: reporterFor(opened.materialId, {
          fetch: fetchNow,
          viewerKey,
          outbox,
          trackId,
          ...(seed ? { seed } : {}),
          onProgress: (progress) => {
            if (currentViewer.current === s.viewerKey) watched(opened.materialId, trackId, progress, s.viewerKey);
          },
          onDropped: (code, status) => {
            trackTraining("training_video_error", { material_id: opened.materialId, code, status });
            // The track ended while it played: it plays on, but nothing more is counted.
            if (code === "track_expired" && session.current === s && !s.expiredNotice) {
              s.expiredNotice = true;
              toast.error(TRACK_EXPIRED);
              refresh();
            }
            if (code === "viewer_changed" && session.current === s) {
              clearOutboxes();
              clearTimers(s);
              s.closed = true;
              session.current = null;
              set({ kind: "closed" });
              refresh();
            }
          },
          onStatus: (status) => {
            if (session.current === s) setSaveStatus(status);
          },
        }),
      };
      session.current = s;
      setItem(opened);
      set({ kind: "loading" });
      trackTraining("training_video_opened", {
        material_id: opened.materialId,
        track_id: trackId,
        resumed: (opened.video?.watchedPercent ?? 0) > 0,
      });
      void load(s);
    },
    [load, refresh, set, viewerKey, watched],
  );

  const close = useCallback(() => {
    const s = session.current;
    if (s && !s.closed) end(s);
    set({ kind: "closed" });
  }, [end, set]);

  // Ready can survive a new account's list response. Retire the previous
  // player's callbacks and queues before any more of its ranges are reported.
  useEffect(() => {
    if (previousViewer.current === viewerKey) return;
    const previous = previousViewer.current;
    previousViewer.current = viewerKey;
    if (previous === null) return;
    clearOutboxes();
    const s = session.current;
    if (s) { clearTimers(s); s.closed = true; }
    session.current = null;
    setItem(null);
    set({ kind: "closed" });
  }, [viewerKey, set]);

  // Pagehide and a hidden tab send what is queued with keepalive; the player
  // reports its open stretch on the same events, which then goes at once too.
  useEffect(() => {
    const hide = () => session.current?.reporter.hide();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") hide();
      else if (session.current && !session.current.closed) session.current.reporter.show();
    };
    window.addEventListener("pagehide", hide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", hide);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Closing on unmount too, so a page change never strands a batch.
  useEffect(
    () => () => {
      const s = session.current;
      if (s && !s.closed) end(s);
    },
    [end],
  );

  const onSegment = useCallback((segment: { start: number; end: number }, positionSeconds: number) => {
    // Also after `close`: the player reports its last stretch as it unmounts,
    // and the closed reporter sends it at once, with keepalive.
    const s = session.current;
    if (!s || s.viewerKey !== currentViewer.current) return;
    s.position = positionSeconds;
    // Its track has ended: it plays, but Gatekeeper would refuse what was watched.
    if (s.item.locked || isPreview()) return;
    s.reporter.add([segment.start, segment.end], positionSeconds);
  }, []);

  const onStop = useCallback(() => {
    const s = session.current;
    if (s && !s.closed) s.reporter.flush();
  }, []);

  const onError = useCallback(
    (positionSeconds: number) => {
      const s = session.current;
      if (!s || s.closed || latest.current.kind !== "playing") return;
      s.position = positionSeconds;
      const at = Date.now();
      s.refreshes = s.refreshes.filter((t) => at - t < 60_000);
      if (s.refreshes.length >= MAX_REFRESHES_PER_MINUTE) {
        trackTraining("training_video_error", { material_id: s.item.materialId, code: "media_error", status: 0 });
        clearTimers(s);
        set({ kind: "failed", retrying: false });
        return;
      }
      s.refreshes.push(at);
      void load(s);
    },
    [load, set],
  );

  const tryAgain = useCallback(() => {
    const s = session.current;
    if (!s || s.closed || latest.current.kind !== "failed") return;
    s.refreshes = [];
    set({ kind: "failed", retrying: true });
    void load(s);
  }, [load, set]);

  const signIn = useCallback(() => {
    const deps = browserDeps();
    beginSignIn(deps.returnTo, deps.storage, deps.navigate, deps.now, TRAINING_SIGNIN_ATTEMPT_KEY);
  }, []);

  const notice: VideoNotice =
    view.kind === "closed" ? null : refreshSignIn || saveStatus === "signin" ? "signin" : saveStatus === "failing" ? "not_saved" : null;

  return { view, item, notice, open, close, onSegment, onStop, onError, tryAgain, signIn };
}
